import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { requisitionsApi } from "../api/requisitions";

export function useRequisitions(filters?: Record<string, string>) {
  return useQuery({
    queryKey: ["requisitions", filters],
    queryFn: () => requisitionsApi.list(filters),
  });
}

export function useRequisition(id: string) {
  return useQuery({
    queryKey: ["requisitions", id],
    queryFn: () => requisitionsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: requisitionsApi.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["requisitions"] }),
  });
}

export function useSubmitRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => requisitionsApi.submit(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["requisitions"] });
      qc.invalidateQueries({ queryKey: ["requisitions", id] });
    },
  });
}

export function useApproveRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, overrides, extra }: {
      id: string; overrides?: unknown; extra?: Record<string, string>;
    }) => requisitionsApi.approve(id, overrides, extra),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["requisitions"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useRejectRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      requisitionsApi.reject(id, comment),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["requisitions"] }),
  });
}

export function useReopenRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => requisitionsApi.reopen(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["requisitions"] });
      qc.invalidateQueries({ queryKey: ["requisitions", id] });
    },
  });
}

export function useUpdateRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: unknown }) =>
      requisitionsApi.update(id, data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["requisitions"] });
      qc.invalidateQueries({ queryKey: ["requisitions", id] });
    },
  });
}

export function usePendingRequisitionsCount(enabled = true) {
  return useQuery({
    queryKey: ["requisitions", "pending-count"],
    queryFn: () =>
      apiClient
        .get<{ data: { count: number } }>("/requisitions/pending-count/")
        .then((r) => r.data.data.count),
    enabled,
  });
}

export function useRequisitionAuditLogs(id: string) {
  return useQuery({
    queryKey: ["requisitions", id, "audit-logs"],
    queryFn: () => requisitionsApi.getAuditLogs(id),
    enabled: !!id,
  });
}

export function useDeleteRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => requisitionsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["requisitions"] }),
  });
}

export function useDeleteRequisitionAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fileId }: { id: string; fileId: string }) =>
      requisitionsApi.deleteAttachment(id, fileId),
    onSuccess: (_data, { id }) => qc.invalidateQueries({ queryKey: ["requisitions", id] }),
  });
}

export function useAddRequisitionAuditLog(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { description: string; mentionIds?: string[] }) =>
      requisitionsApi.addAuditLog(id, payload.description, payload.mentionIds ?? []),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["requisitions", id, "audit-logs"] }),
  });
}
